import React, { useState } from 'react';
import SpectrumChart from './SpectrumChart';
import ResultsTable from './ResultsTable';
import audioBuzzAPI from '../../services/audioBuzzApi';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const AnalysisResults = ({ results, parameters }) => {
  const [generatingImages, setGeneratingImages] = useState({});
  const [filterType, setFilterType] = useState('all'); // 'all', 'buzz', 'no-buzz'
  const [searchQuery, setSearchQuery] = useState('');

  const exportToExcel = () => {
    if (results.length === 0) return;
    
    const exportData = results.map(result => ({
      'Filename': result.filename,
      'True Label': result.true_label || result.trueLabel,
      'Prediction': result.prediction,
      'Prediction Binary': result.prediction_binary,
      'Base Frequency (Hz)': result.base_frequency || result.baseFreq,
      'Base dB': result.base_db?.toFixed(1) || result.basedB?.toFixed(1),
      'Mean (excluding base)': result.mean,
      'Standard Deviation': result.std,
      '±1σ Interval': result.metrics?.sigma_interval || result.interval,
      'Above 1σ Count': result.above_count || result.aboveCount,
      'Below 1σ Count': result.below_count || result.belowCount,
      'Strong Count': result.strong_count || result.strongCount,
      'Analyzed At': result.analyzed_at
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Analysis Results');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Buzz_Analysis_Results.xlsx');
  };

  const downloadAllReportsAsImages = async () => {
    if (results.length === 0) return;

    setGeneratingImages(prev => ({ ...prev, 'all': true }));

    try {
      const imageBlobs = [];
      
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (!result.success) continue;

        // Create a TXT file from FULL spectrum data (not just harmonics)
        const spectrumData = result.spectrum_data || result.spectrumData;
        if (!spectrumData || spectrumData.length === 0) {
          console.warn(`Skipping ${result.filename}: No spectrum data available`);
          continue;
        }

        const txtContent = spectrumData.map(point => 
          `${point.frequency || point.Freq}\t${point.db_level || point.dB}`
        ).join('\n');
        
        const txtBlob = new Blob([`Frequency (Hz)\tLevel (dB)\n${txtContent}`], 
          { type: 'text/plain' });
        const txtFile = new File([txtBlob], result.filename.replace(/\.(wav|txt)$/i, '.txt'));

        const imageBlob = await audioBuzzAPI.generateCnnImage(txtFile, {
          baseFreq: result.base_frequency || result.baseFreq,
          nHarmonics: 10,
          fillBlue: true,
          showMetrics: true
        });

        imageBlobs.push({
          blob: imageBlob,
          filename: result.filename.replace(/\.(wav|txt)$/i, '_report.png')
        });
      }

      // Create ZIP file with all images
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      imageBlobs.forEach(({ blob, filename }) => {
        zip.file(filename, blob);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, 'All_Analysis_Reports.zip');

    } catch (error) {
      console.error('Failed to generate report images:', error);
      alert(`Failed to generate report images: ${error.message}`);
    } finally {
      setGeneratingImages(prev => ({ ...prev, 'all': false }));
    }
  };

  const generateCnnImage = async (result, index) => {
    if (!result.success) return;

    setGeneratingImages(prev => ({ ...prev, [index]: true }));

    try {
      // Create a TXT file from FULL spectrum data (not just harmonics)
      const spectrumData = result.spectrum_data || result.spectrumData;
      if (!spectrumData || spectrumData.length === 0) {
        throw new Error('No spectrum data available for CNN image generation');
      }

      const txtContent = spectrumData.map(point => 
        `${point.frequency || point.Freq}\t${point.db_level || point.dB}`
      ).join('\n');
      
      const txtBlob = new Blob([`Frequency (Hz)\tLevel (dB)\n${txtContent}`], 
        { type: 'text/plain' });
      const txtFile = new File([txtBlob], result.filename.replace(/\.(wav|txt)$/i, '.txt'));

      const imageBlob = await audioBuzzAPI.generateCnnImage(txtFile, {
        baseFreq: result.base_frequency || result.baseFreq,
        nHarmonics: 10,
        fillBlue: true,
        showMetrics: true
      });

      // Download the generated image
      audioBuzzAPI.downloadBlob(imageBlob, 
        result.filename.replace(/\.(wav|txt)$/i, '_cnn.png'));

    } catch (error) {
      console.error('Failed to generate CNN image:', error);
      alert(`Failed to generate CNN image: ${error.message}`);
    } finally {
      setGeneratingImages(prev => ({ ...prev, [index]: false }));
    }
  };

  // Filter results based on selected filter and search query
  const filteredResults = results.filter(result => {
    // Filter by buzz/no-buzz
    const predictionMatch = 
      filterType === 'all' ? true :
      filterType === 'buzz' ? (result.prediction !== 'No Buzz' && result.prediction_binary !== 'No Buzz') :
      filterType === 'no-buzz' ? (result.prediction === 'No Buzz' || result.prediction_binary === 'No Buzz') :
      true;
    
    // Filter by search query
    const searchMatch = searchQuery === '' || 
      result.filename?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return predictionMatch && searchMatch;
  });

  const buzzCount = results.filter(r => r.prediction !== 'No Buzz' && r.prediction_binary !== 'No Buzz').length;
  const noBuzzCount = results.filter(r => r.prediction === 'No Buzz' || r.prediction_binary === 'No Buzz').length;

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📊</div>
        <p className="text-gray-400 text-lg">No analysis results yet. Upload files and click Analyze.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <h2 className="text-2xl font-bold text-white">📊 Analysis Results ({filteredResults.length} of {results.length} files)</h2>
          <div className="flex gap-3 flex-wrap">
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                generatingImages['all']
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              onClick={downloadAllReportsAsImages}
              disabled={generatingImages['all']}
              title="Download all analysis reports as images in a ZIP file"
            >
              {generatingImages['all'] ? '⏳ Generating ZIP...' : '📦 Download All Reports as Images'}
            </button>
            {parameters.exportExcel && (
              <button 
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200"
                onClick={exportToExcel}
              >
                📄 Export to Excel
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => setFilterType('buzz')}
            className={`rounded-lg p-4 text-center border transition-all duration-200 ${
              filterType === 'buzz'
                ? 'bg-red-900/50 border-red-700 ring-2 ring-red-500'
                : 'bg-red-900/30 border-red-800 hover:bg-red-900/40'
            }`}
          >
            <div className="text-3xl font-bold text-red-400">
              {buzzCount}
            </div>
            <div className="text-sm text-red-300 font-medium">Buzz Detected</div>
          </button>
          <button
            onClick={() => setFilterType('no-buzz')}
            className={`rounded-lg p-4 text-center border transition-all duration-200 ${
              filterType === 'no-buzz'
                ? 'bg-green-900/50 border-green-700 ring-2 ring-green-500'
                : 'bg-green-900/30 border-green-800 hover:bg-green-900/40'
            }`}
          >
            <div className="text-3xl font-bold text-green-400">
              {noBuzzCount}
            </div>
            <div className="text-sm text-green-300 font-medium">No Buzz</div>
          </button>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                filterType === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All ({results.length})
            </button>
            <button
              onClick={() => setFilterType('buzz')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                filterType === 'buzz'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              Buzz ({buzzCount})
            </button>
            <button
              onClick={() => setFilterType('no-buzz')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                filterType === 'no-buzz'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              No Buzz ({noBuzzCount})
            </button>
          </div>
          
          <div className="flex-1 sm:max-w-md">
            <input
              type="text"
              placeholder="🔍 Search by filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {(filterType !== 'all' || searchQuery !== '') && (
            <button
              onClick={() => {
                setFilterType('all');
                setSearchQuery('');
              }}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors duration-200 border border-gray-700"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {filteredResults.length === 0 ? (
        <div className="bg-gray-900 rounded-lg p-12 text-center border border-gray-800">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-white mb-2">No results found</h3>
          <p className="text-gray-400">
            {searchQuery !== '' 
              ? `No files matching "${searchQuery}" found.`
              : `No ${filterType === 'buzz' ? 'buzz' : 'no-buzz'} results found.`
            }
          </p>
          <button
            onClick={() => {
              setFilterType('all');
              setSearchQuery('');
            }}
            className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredResults.map((result, index) => {
            // Find the original index for the generatingImages state
            const originalIndex = results.findIndex(r => r.filename === result.filename);
            return (
          <div key={index} className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800">
            <div className="bg-gray-800 px-6 py-4 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-white">{result.filename}</h3>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    (result.prediction_binary || result.prediction) === 'Audio Buzz' || (result.prediction_binary || result.prediction) === 'Buzz'
                      ? 'bg-red-900/50 text-red-300 border border-red-700'
                      : 'bg-green-900/50 text-green-300 border border-green-700'
                  }`}>
                    {result.prediction_binary || result.prediction}
                  </span>
                </div>
                <button 
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    generatingImages[originalIndex] || !result.success
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                  }`}
                  onClick={() => generateCnnImage(result, originalIndex)}
                  disabled={generatingImages[originalIndex] || !result.success}
                  title="Generate CNN-style harmonic visualization"
                >
                  {generatingImages[originalIndex] ? '⏳ Generating...' : '🖼️ Generate CNN Image'}
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Spectrum Analysis</h4>
                  <SpectrumChart 
                    data={{
                      spectrumData: result.spectrum_data || result.spectrumData,
                      harmonicFreqs: result.harmonics?.map(h => h.frequency) || result.harmonicFreqs,
                      harmonicDBs: result.harmonics?.map(h => h.db_level) || result.harmonicDBs,
                      baseFreq: result.base_frequency || result.baseFreq,
                      basedB: result.base_db || result.basedB,
                      mean: result.mean,
                      std: result.std,
                      thresholdStrong: parameters.thresholdStrong
                    }}
                    title={`${result.filename} | Prediction: ${result.prediction_binary || result.prediction}`}
                  />
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-white mb-4">Harmonic Analysis</h4>
                  <ResultsTable result={result} />
                </div>
              </div>
            </div>
          </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AnalysisResults;
